import apiClient from './client';

export interface RuleCondition {
  field: string;
  operator: string;
  value: unknown;
  source: 'order' | 'user' | 'response';
}

export interface RuleAction {
  type: 'create_order';
  config: {
    template: Record<string, unknown>;
    mapFields?: Record<string, string>;
  };
}

export interface RuleLevel {
  operator: 'AND' | 'OR';
  conditions: RuleCondition[];
  actions: RuleAction[];
  elseLevel?: RuleLevel;
}

export type RuleTrigger =
  | 'on_status_change'
  | 'on_order_creation'
  | 'on_order_completion'
  | 'on_order_failure';

export interface OrderLogicRule {
  urn: string;
  uuid: string;
  name: string;
  description?: string;
  enabled: boolean;
  priority: number;
  trigger: RuleTrigger;
  rootLevel: RuleLevel;
  applicableLenders: string[];
  applicableProducts: string[];
  productCategory?: string;
  triggerStatuses?: string[];
  userGroups?: string[];
  creationDate: string;
  modificationDate: string;
}

export interface CreateOrderLogicRuleRequest {
  name: string;
  description?: string;
  enabled: boolean;
  priority: number;
  trigger: RuleTrigger;
  rootLevel: RuleLevel;
  applicableLenders: string[];
  applicableProducts: string[];
  productCategory?: string;
  triggerStatuses?: string[];
  userGroups?: string[];
}

export async function getOrderLogicRules(trigger?: string): Promise<OrderLogicRule[]> {
  const response = await apiClient.get('/order-logic-rules', {
    params: trigger ? { trigger } : undefined,
  });
  return response.data;
}

/** Extract UUID from order logic rule URN (urn:oms:order_logic_rule:uuid) */
function uuidFromUrn(urn: string): string {
  const parts = urn.split(':');
  return parts[parts.length - 1] || urn;
}

export async function getOrderLogicRule(urn: string): Promise<OrderLogicRule> {
  const uuid = uuidFromUrn(urn);
  const response = await apiClient.get(`/order-logic-rules/by-uuid/${uuid}`);
  return response.data;
}

export async function createOrderLogicRule(
  data: CreateOrderLogicRuleRequest,
): Promise<OrderLogicRule> {
  const response = await apiClient.post('/order-logic-rules', data);
  return response.data;
}

export async function updateOrderLogicRule(
  urn: string,
  data: Partial<CreateOrderLogicRuleRequest>,
): Promise<OrderLogicRule> {
  const uuid = uuidFromUrn(urn);
  const response = await apiClient.put(`/order-logic-rules/by-uuid/${uuid}`, data);
  return response.data;
}

export async function deleteOrderLogicRule(urn: string): Promise<void> {
  const uuid = uuidFromUrn(urn);
  await apiClient.delete(`/order-logic-rules/by-uuid/${uuid}`);
}

// ─── Simulation & AI types ───

export interface ConditionResult {
  condition: string;
  source: string;
  field: string;
  operator: string;
  expectedValue: unknown;
  actualValue: unknown;
  passed: boolean;
}

export interface SimulationResultItem {
  rule: {
    urn?: string;
    name: string;
    trigger: string;
    priority: number;
    description?: string;
    enabled: boolean;
  };
  matched: boolean;
  conditionResults: ConditionResult[];
  matchedActions: Array<{ type: string; config: unknown }>;
}

export interface SimulateRequest {
  orderData: Record<string, unknown>;
  userData: Record<string, unknown>;
  responseData: Record<string, unknown>;
}

export interface SamplePayload {
  order: Record<string, unknown>;
  user: Record<string, unknown>;
  response: Record<string, unknown>;
}

// ─── Simulation & AI API calls ───

export async function simulateRules(data: SimulateRequest): Promise<SimulationResultItem[]> {
  const response = await apiClient.post('/oms/orders/rules-simulator/simulate', data);
  return response.data;
}

export async function generatePayload(
  request: { ruleUrn?: string; rule?: Partial<CreateOrderLogicRuleRequest> },
): Promise<SamplePayload> {
  const response = await apiClient.post('/oms/orders/rules-simulator/ai/generate-payload', request);
  return response.data;
}

export async function generateRuleFromDescription(
  description: string,
): Promise<Partial<CreateOrderLogicRuleRequest>> {
  const response = await apiClient.post('/oms/orders/rules-simulator/ai/generate-rule', { description });
  return response.data;
}
